import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { type ProspectTaskDto } from "@crowdcoursing/api/openapi";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import {
  alpha,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";

import { US_TIMEZONES } from "~constants/timezones";
import { type TaskType } from "~hooks/useTaskDashboard";
import { type EventSummary, type RoomTaskSummary } from "~services/graphql/generated/react-query";

import { PageHeader } from "../../atoms/PageHeader/PageHeader";
import { TaskCard } from "../../atoms/TaskCard/TaskCard";
import { TaskContent } from "../../molecules/TaskContent/TaskContent";
import { type CarouselTaskItem, combineTasks, type ProspectEnrichmentData } from "./taskUtils";

// Centralized strings for internationalization
const tAllDone = "All Done! 🎉";
const tTasksCompleted = "You have completed all your tasks. Great job!";
const tAll = "All";
const tRegion = "Region";
const tTimeZone = "Time Zone";

// Local storage keys for filter preferences
const REGION_STORAGE_KEY = "taskDashboard.selectedRegion";
const TIMEZONE_STORAGE_KEY = "taskDashboard.selectedTimeZone";

/**
 * Gets the initial region from localStorage, validating it still exists in available regions.
 * Falls back to "All" if the stored value doesn't exist or is invalid.
 */
const getInitialRegion = (availableRegions: string[]): string => {
  const stored = localStorage.getItem(REGION_STORAGE_KEY);
  if (stored && stored !== tAll && availableRegions.includes(stored)) {
    return stored;
  }
  return tAll;
};

/**
 * Gets the initial timezone from localStorage, validating it still exists in available timezones.
 * Falls back to "All" if the stored value doesn't exist or is invalid.
 */
const getInitialTimeZone = (): string => {
  const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
  if (stored && stored !== tAll && US_TIMEZONES.some((tz) => tz.value === stored)) {
    return stored;
  }
  return tAll;
};

export interface TaskDashboardTemplateProps {
  currentTaskType: TaskType;
  onTaskTypeChange: (taskType: TaskType) => void;
  salesTasks: {
    myTasks: ProspectTaskDto[];
    tasksDueSoon: ProspectTaskDto[];
    isLoading: boolean;
    handleCompleteTask: (taskId: string) => void;
    handleDeleteTask: (taskId: string) => void;
  };
  roomTasks?: {
    myTasks: RoomTaskSummary[];
    isLoading: boolean;
  };
  eventTasks?: {
    myTasks: EventSummary[];
    isLoading: boolean;
  };
  onTaskSelect?: (task: CarouselTaskItem) => void;
  selectedTask?: CarouselTaskItem | null;
  taskContentArea?: ReactNode;
  /** Map of prospect ID to enrichment data (region, timeZone) for displaying as chips on sales task cards */
  prospectsMap?: Map<string, ProspectEnrichmentData>;
  /** List of unique regions available for filtering */
  regions?: string[];
}

export const TaskDashboardTemplate = ({
  currentTaskType,
  onTaskTypeChange,
  salesTasks,
  roomTasks,
  eventTasks,
  onTaskSelect,
  taskContentArea,
  selectedTask,
  prospectsMap,
  regions = [],
}: TaskDashboardTemplateProps) => {
  // Track which tasks are currently being completed (by their ID)
  const [completingTaskIds, setCompletingTaskIds] = useState<string[]>([]);
  const sliderRef = useRef<Slider>(null);

  // Filter state - initialized from localStorage with validation
  const [selectedRegion, setSelectedRegion] = useState<string>(() => getInitialRegion(regions));
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>(getInitialTimeZone);

  // Re-validate region when regions prop loads/changes (since it comes from API)
  useEffect(() => {
    if (regions.length > 0) {
      const stored = localStorage.getItem(REGION_STORAGE_KEY);
      if (stored && stored !== tAll && regions.includes(stored)) {
        // Stored region is valid, ensure it's selected
        setSelectedRegion(stored);
      } else if (stored && stored !== tAll && !regions.includes(stored)) {
        // Stored region no longer exists, reset to "All"
        setSelectedRegion(tAll);
        localStorage.setItem(REGION_STORAGE_KEY, tAll);
      }
    }
  }, [regions]);

  // Check if a specific task is currently being completed
  const isTaskCompleting = (taskId?: string) => {
    return taskId ? completingTaskIds.includes(taskId) : false;
  };

  // Handle filter changes and persist to localStorage
  const handleRegionChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedRegion(value);
    localStorage.setItem(REGION_STORAGE_KEY, value);
  };

  const handleTimeZoneChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedTimeZone(value);
    localStorage.setItem(TIMEZONE_STORAGE_KEY, value);
  };

  // Handler for completing tasks based on task type
  const handleCompleteTask = (task: CarouselTaskItem) => async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card selection

    if (!task.id) {
      return;
    }

    // Add this task ID to the completing list
    setCompletingTaskIds((prev) => [...prev, task.id ?? ""]);

    try {
      switch (task.type) {
        case "sales":
          await salesTasks.handleCompleteTask(task.id);
          break;
        case "room":
          // Will be implemented when room tasks are added
          console.log("Complete room task:", task.id);
          // Simulate API call for demo purposes
          await new Promise((resolve) => setTimeout(resolve, 1000));
          break;
        case "event":
          // Will be implemented when event tasks are added
          console.log("Complete event task:", task.id);
          // Simulate API call for demo purposes
          await new Promise((resolve) => setTimeout(resolve, 1000));
          break;
        default:
          console.error("Unknown task type:", task.type);
      }

      // Reload the window after task completion
      window.location.reload();
    } catch (error) {
      console.error("Error completing task:", error);
    } finally {
      // Remove this task ID from the completing list
      setCompletingTaskIds((prev) => prev.filter((id) => id !== task.id));
    }
  };

  // Combine all tasks for the carousel using the utility function
  const allTasksUnfiltered: CarouselTaskItem[] = useMemo(
    () => combineTasks(salesTasks.myTasks || [], roomTasks?.myTasks || [], eventTasks?.myTasks || [], prospectsMap),
    [salesTasks.myTasks, roomTasks?.myTasks, eventTasks?.myTasks, prospectsMap],
  );

  // Filter tasks based on selected region and timeZone
  const allTasks: CarouselTaskItem[] = useMemo(() => {
    return allTasksUnfiltered.filter((task) => {
      // Only filter sales tasks (they have prospect data with region/timeZone)
      if (task.type !== "sales") {
        return true;
      }

      // Get prospect data for filtering
      const prospectData = task.prospectId && prospectsMap ? prospectsMap.get(task.prospectId) : undefined;

      // Check region filter
      if (selectedRegion !== tAll) {
        if (!prospectData?.region || prospectData.region !== selectedRegion) {
          return false;
        }
      }

      // Check timeZone filter
      if (selectedTimeZone !== tAll) {
        if (!prospectData?.timeZone || prospectData.timeZone !== selectedTimeZone) {
          return false;
        }
      }

      return true;
    });
  }, [allTasksUnfiltered, prospectsMap, selectedRegion, selectedTimeZone]);

  // Check if there are no tasks at all (after filtering)
  const hasNoTasks = allTasks.length === 0;

  const handleTaskSelect = (task: CarouselTaskItem) => {
    // Change the task type if needed
    if (task.type !== currentTaskType) {
      onTaskTypeChange(task.type);
    }

    // Call the onTaskSelect callback if provided
    if (onTaskSelect) {
      onTaskSelect(task);
    }
  };

  // Find the index of the selected task
  const selectedIndex = selectedTask ? allTasks.findIndex((task) => task.id === selectedTask.id) : -1;
  const initialSlide = selectedIndex >= 0 ? Math.floor(selectedIndex / 3) * 3 : 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Desktop slider settings
  const desktopSettings = {
    arrows: false, // We'll use our custom arrows
    dots: true,
    dotsClass: "slick-dots slick-single-line-dots",
    infinite: false,
    initialSlide,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          initialSlide: selectedIndex >= 0 ? Math.floor(selectedIndex / 2) * 2 : 0,
          slidesToScroll: 2,
          slidesToShow: 2,
        },
      },
    ],
    slidesToScroll: 3,
    slidesToShow: 3,
    speed: 500,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 10,
  };

  // No mobile slider settings needed with our simplified approach

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  // No mobile slider navigation functions needed with our simplified approach

  return (
    <Box
      sx={{
        maxWidth: "100%",
        overflowX: "hidden",
        p: { sm: 2, xs: 1 },
      }}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <PageHeader title="Task Dashboard" icon={<ChecklistRoundedIcon data-variant="page-header" />} />

        {/* Filter dropdowns for Region and Time Zone */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {regions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="region-filter-label">{tRegion}</InputLabel>
              <Select
                labelId="region-filter-label"
                id="region-filter"
                value={selectedRegion}
                label={tRegion}
                onChange={handleRegionChange}
              >
                <MenuItem value={tAll}>{tAll}</MenuItem>
                {regions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="timezone-filter-label">{tTimeZone}</InputLabel>
            <Select
              labelId="timezone-filter-label"
              id="timezone-filter"
              value={selectedTimeZone}
              label={tTimeZone}
              onChange={handleTimeZoneChange}
            >
              <MenuItem value={tAll}>{tAll}</MenuItem>
              {US_TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {hasNoTasks ? (
          <AllDoneBanner>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, justifyContent: "center" }}>
              <Typography variant="h4" component="h2" color="primary.main" fontWeight="bold">
                {tAllDone}
              </Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {tTasksCompleted}
            </Typography>
          </AllDoneBanner>
        ) : (
          <CarouselSection>
            {isMobile ? (
              // Simple mobile carousel - one task at a time, no dots
              <Box sx={{ position: "relative", px: 1, width: "100%" }}>
                {/* Simple mobile view - just show the current task */}
                {allTasks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <TaskCard
                      onClick={() => handleTaskSelect(selectedTask ? selectedTask : allTasks[0])}
                      isSelected={true}
                      onComplete={handleCompleteTask(selectedTask ? selectedTask : allTasks[0])}
                      isCompleting={isTaskCompleting(selectedTask ? selectedTask.id : allTasks[0].id)}
                    >
                      <TaskContent task={selectedTask ? selectedTask : allTasks[0]} />
                    </TaskCard>
                  </Box>
                )}
                {allTasks.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        const currentIndex = selectedTask
                          ? allTasks.findIndex((task) => task.id === selectedTask.id)
                          : 0;
                        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allTasks.length - 1;
                        handleTaskSelect(allTasks[prevIndex]);
                      }}
                      size="small"
                      aria-label="Previous task"
                      sx={{ boxShadow: 1 }}
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        const currentIndex = selectedTask
                          ? allTasks.findIndex((task) => task.id === selectedTask.id)
                          : 0;
                        const nextIndex = currentIndex < allTasks.length - 1 ? currentIndex + 1 : 0;
                        handleTaskSelect(allTasks[nextIndex]);
                      }}
                      size="small"
                      aria-label="Next task"
                      sx={{ boxShadow: 1 }}
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ) : (
              // Desktop Carousel
              <FullWidthContainer>
                {/* Left arrow positioned at the left edge */}
                <EdgeArrow onClick={goToPrev} aria-label="Previous task" className="left-arrow">
                  <ArrowBackIosNewIcon />
                </EdgeArrow>

                {/* Center container for the carousel content */}
                <CenterContainer>
                  <StyledSlider ref={sliderRef} {...desktopSettings}>
                    {allTasks.map((task) => (
                      <div key={task.id} style={{ height: "100%", padding: "0 8px" }}>
                        <TaskCard
                          onClick={() => handleTaskSelect(task)}
                          isSelected={task.id === selectedTask?.id}
                          onComplete={handleCompleteTask(task)}
                          isCompleting={isTaskCompleting(task.id)}
                        >
                          <TaskContent task={task} />
                        </TaskCard>
                      </div>
                    ))}
                  </StyledSlider>
                </CenterContainer>

                {/* Right arrow positioned at the right edge */}
                <EdgeArrow onClick={goToNext} aria-label="Next task" className="right-arrow">
                  <ArrowForwardIosIcon />
                </EdgeArrow>
              </FullWidthContainer>
            )}
          </CarouselSection>
        )}

        {/* Task content area - displays the selected task details */}
        {taskContentArea && <Box>{taskContentArea}</Box>}
      </Stack>
    </Box>
  );
};

// No mobile slider styled components needed with our simplified approach

// Desktop styled components for the carousel
const StyledSlider = styled(Slider)(({ theme }) => ({
  ".slick-dots": {
    "& li": {
      margin: "0 3px",
      width: "auto",
    },
    "& li button:before": {
      fontSize: "10px",
      opacity: 0.5,
    },
    "& li.slick-active button:before": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    bottom: "-60px",
    display: "flex !important",
    flexWrap: "nowrap",
    justifyContent: "center",
    overflow: "auto",
    padding: theme.spacing(1, 0),
    whiteSpace: "nowrap",
  },
  ".slick-list": {
    overflow: "hidden",
  },
  ".slick-slide": {
    height: "auto",
    padding: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      display: "flex !important",
      justifyContent: "center",
    },
  },
  ".slick-track": {
    alignItems: "stretch",
    display: "flex",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      display: "flex !important",
      justifyContent: "center",
    },
  },
  width: "100%",
}));

const CarouselSection = styled(Box)(({ theme }) => ({
  "&::after": {
    backgroundColor: theme.palette.divider,
    content: '""',
    height: "1px",
    left: 0,
    position: "absolute",
    right: 0,
  },
  margin: theme.spacing(3, 0, 6),
  position: "relative",
}));

// Full-width container that spans the entire viewport width
const FullWidthContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  left: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",
  position: "relative",
  right: "50%",
  width: "100vw",
}));

// Center container for the carousel content
const CenterContainer = styled(Box)(({ theme }) => ({
  // Add bottom padding for dots
  boxSizing: "border-box",
  flex: 1,
  margin: "0 auto",
  maxWidth: "1200px",
  padding: theme.spacing(0, 2, 8, 2),
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 0, 4, 0),
  },
}));

// Edge-positioned arrows
const EdgeArrow = styled(IconButton)(({ theme }) => ({
  "& svg": {
    color: theme.palette.text.primary,
    fontSize: "1.6rem",
  },
  "&.left-arrow": {
    left: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      left: theme.spacing(5),
    },
  },
  "&.right-arrow": {
    right: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      right: theme.spacing(5),
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[6],
  },
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[4],
  height: 50,

  position: "absolute",
  // Hide arrows on mobile devices
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },

  top: "calc(50% - 40px)",

  // Adjust to account for dots
  transform: "translateY(-50%)",

  width: 50,
  zIndex: 10,
}));

// Styled component for the "All Done!" banner
const AllDoneBanner = styled(Paper)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  borderRadius: (theme.shape.borderRadius as number) * 2,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(3, 0, 6),
  padding: theme.spacing(4),
  position: "relative",
  textAlign: "center",
}));
